import { useState, useMemo, useCallback, useEffect } from "react";
import { useFamily } from "@/hooks/useFamily";
import { useAllChildren } from "@/hooks/useAllChildren";
import { useAuth } from "@/hooks/useAuth";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { getDateString, getDayRange, formatDate, formatDateLong } from "@/lib/family-date";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Plus, Trash2 } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

// ── Types ──────────────────────────────────────────────
interface Child {
  id: string;
  name: string;
  avatar_emoji: string;
  family_id: string;
}

interface CustodyDay {
  id: string;
  child_id: string;
  date: string;
  custodial_parent_user_id: string;
  is_handover: boolean;
  handover_type: string | null;
  handover_time: string | null;
  handover_note: string | null;
  family_id: string;
}

interface ExchangeChecklist {
  id: string;
  template_id: string;
  family_id: string;
  child_id: string | null;
  exchange_at: string;
  created_by: string;
  created_at: string;
  from_parent_user_id: string | null;
  to_parent_user_id: string | null;
}

interface ExchangeLine {
  id: string;
  exchange_checklist_id: string;
  label: string;
  sort_order: number;
  added_by_user_id: string | null;
  created_at: string;
}

interface LineCheck {
  id: string;
  exchange_checklist_id: string;
  exchange_checklist_line_id: string;
  checked: boolean;
  checked_by: string | null;
  checked_at: string | null;
}

interface ChecklistTemplate {
  id: string;
  name: string;
  family_id: string;
  child_id: string | null;
}

interface FamilyMember {
  user_id: string;
  display_name: string | null;
  role: string;
}

// ── Helpers ────────────────────────────────────────────
function addDaysToDateStr(dateStr: string, n: number, tz: string = "Europe/Oslo"): string {
  const [y, m, d] = dateStr.split("-").map(Number);
  const dt = new Date(y, m - 1, d + n);
  return getDateString(dt, tz);
}

function buildExchangeAt(dateStr: string, handoverTime: string | null, tz: string = "Europe/Oslo"): string {
  const time = handoverTime || "12:00";
  const [y, m, d] = dateStr.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  const { start } = getDayRange(dt, tz);
  const offsetMatch = start.match(/([+-]\d{2}:\d{2})$/);
  const offset = offsetMatch ? offsetMatch[1] : "+01:00";
  return `${dateStr}T${time}:00${offset}`;
}

const keys = {
  templates: (fid: string) => ["checklist-templates", fid] as const,
  handovers: (fid: string, childId: string) => ["upcoming-handovers", fid, childId] as const,
  custodyDay: (childId: string, date: string) => ["custody-day", childId, date] as const,
  exchangeForDate: (fid: string, childId: string, date: string) => ["exchange-for-date", fid, childId, date] as const,
  exchanges: (fid: string, childId: string) => ["exchanges", fid, childId] as const,
  lines: (exchangeId: string) => ["exchange-lines", exchangeId] as const,
  checks: (exchangeId: string) => ["exchange-checks", exchangeId] as const,
  members: (fid: string) => ["family-members", fid] as const,
};

export interface ExchangeSectionProps {
  /** When true, shown inside Samarbeid page; copy and links refer to same-page calendar */
  embeddedInSamarbeid?: boolean;
  /** When provided, use this child ID instead of internal tab state */
  selectedChildId?: string;
}

export function ExchangeSection({ embeddedInSamarbeid = false, selectedChildId }: ExchangeSectionProps) {
  const { timezone } = useFamily();
  const { children: allChildren, splitChildIds } = useAllChildren();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const children = allChildren.filter((c) => splitChildIds.has(c.id));

  const [selectedHandoverDate, setSelectedHandoverDate] = useState<string | null>(null);
  const [showExchangePicker, setShowExchangePicker] = useState(false);
  const [newLineText, setNewLineText] = useState("");

  const todayStr = getDateString(new Date(), timezone);

  const [internalChildId, setInternalChildId] = useState<string | null>(() => children[0]?.id ?? null);
  useEffect(() => {
    if (internalChildId === null && children.length > 0) setInternalChildId(children[0].id);
  }, [children.length, internalChildId]);

  // Use prop if provided, otherwise use internal state
  const activeChildId = selectedChildId ?? internalChildId;
  const setActiveChildId = (id: string) => {
    if (!selectedChildId) setInternalChildId(id);
  };
  const activeChild = children.find((c) => c.id === activeChildId);
  const fid = activeChild?.family_id ?? "";

  const { data: members = [] } = useQuery({
    queryKey: keys.members(fid),
    queryFn: async () => {
      const { data, error } = await supabase
        .from("family_members")
        .select("user_id, display_name, role")
        .eq("family_id", fid);
      if (error) throw error;
      return data as FamilyMember[];
    },
    enabled: !!fid,
  });

  function getParentLabel(userId: string | null): string {
    if (!userId) return "Ukjent";
    if (userId === user?.id) return "Deg";
    const m = members.find((m) => m.user_id === userId);
    return m?.display_name || "Medforelder";
  }

  const { data: templates = [] } = useQuery({
    queryKey: keys.templates(fid),
    queryFn: async () => {
      const { data, error } = await supabase
        .from("checklist_templates")
        .select("*")
        .eq("family_id", fid);
      if (error) throw error;
      return data as ChecklistTemplate[];
    },
    enabled: !!fid,
  });

  const activeTemplate =
    templates.find((t) => t.child_id === activeChildId) ?? templates.find((t) => !t.child_id) ?? null;

  useQuery({
    queryKey: ["checklist-items", activeTemplate?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("checklist_items")
        .select("*")
        .eq("template_id", activeTemplate!.id)
        .order("sort_order");
      if (error) throw error;
      return data;
    },
    enabled: !!activeTemplate?.id,
  });

  const { data: upcomingHandovers = [] } = useQuery({
    queryKey: keys.handovers(fid, activeChildId ?? ""),
    queryFn: async () => {
      const { data, error } = await supabase
        .from("custody_days")
        .select("*")
        .eq("family_id", fid)
        .eq("child_id", activeChildId!)
        .eq("is_handover", true)
        .gte("date", todayStr)
        .order("date", { ascending: true })
        .limit(5);
      if (error) throw error;
      return data as CustodyDay[];
    },
    enabled: !!fid && !!activeChildId,
  });

  const activeHandoverDate = selectedHandoverDate ?? upcomingHandovers[0]?.date ?? null;
  const activeHandover = upcomingHandovers.find((h) => h.date === activeHandoverDate) ?? null;
  const dayBeforeStr = activeHandoverDate ? addDaysToDateStr(activeHandoverDate, -1, timezone) : null;

  const { data: dayBeforeCustody } = useQuery({
    queryKey: keys.custodyDay(activeChildId ?? "", dayBeforeStr ?? ""),
    queryFn: async () => {
      const { data, error } = await supabase
        .from("custody_days")
        .select("custodial_parent_user_id")
        .eq("child_id", activeChildId!)
        .eq("date", dayBeforeStr!)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!activeChildId && !!dayBeforeStr,
  });

  const fromParent = dayBeforeCustody?.custodial_parent_user_id ?? null;
  const toParent = activeHandover?.custodial_parent_user_id ?? null;

  const { data: exchangeForDate } = useQuery({
    queryKey: keys.exchangeForDate(fid, activeChildId ?? "", activeHandoverDate ?? ""),
    queryFn: async () => {
      const [y, m, d] = activeHandoverDate!.split("-").map(Number);
      const dt = new Date(y, m - 1, d);
      const { start } = getDayRange(dt, timezone);
      const dtNext = new Date(y, m - 1, d + 1);
      const { start: endISO } = getDayRange(dtNext, timezone);
      const { data, error } = await supabase
        .from("exchange_checklists")
        .select("*")
        .eq("family_id", fid)
        .eq("child_id", activeChildId!)
        .gte("exchange_at", start)
        .lt("exchange_at", endISO)
        .maybeSingle();
      if (error) throw error;
      return data as ExchangeChecklist | null;
    },
    enabled: !!fid && !!activeChildId && !!activeHandoverDate,
  });

  const { data: childExchanges = [] } = useQuery({
    queryKey: keys.exchanges(fid, activeChildId ?? ""),
    queryFn: async () => {
      const { data, error } = await supabase
        .from("exchange_checklists")
        .select("*")
        .eq("family_id", fid)
        .eq("child_id", activeChildId!)
        .order("exchange_at", { ascending: false })
        .limit(20);
      if (error) throw error;
      return data as ExchangeChecklist[];
    },
    enabled: !!fid && !!activeChildId,
  });

  const [historyExchangeId, setHistoryExchangeId] = useState<string | null>(null);
  const activeExchangeId = historyExchangeId ?? exchangeForDate?.id ?? null;
  const activeExchange =
    historyExchangeId ? childExchanges.find((e) => e.id === historyExchangeId) ?? null : exchangeForDate ?? null;

  const { data: lines = [] } = useQuery({
    queryKey: keys.lines(activeExchangeId ?? ""),
    queryFn: async () => {
      const { data, error } = await supabase
        .from("exchange_checklist_lines")
        .select("*")
        .eq("exchange_checklist_id", activeExchangeId!)
        .order("sort_order");
      if (error) throw error;
      return data as ExchangeLine[];
    },
    enabled: !!activeExchangeId,
  });

  const { data: checks = [] } = useQuery({
    queryKey: keys.checks(activeExchangeId ?? ""),
    queryFn: async () => {
      const { data, error } = await supabase
        .from("exchange_checklist_line_checks")
        .select("*")
        .eq("exchange_checklist_id", activeExchangeId!);
      if (error) throw error;
      return data as LineCheck[];
    },
    enabled: !!activeExchangeId,
  });

  const checkByLineId = useMemo(
    () => new Map(checks.map((c) => [c.exchange_checklist_line_id, c])),
    [checks]
  );

  const createMutation = useMutation({
    mutationFn: async () => {
      if (!activeHandoverDate || !activeChildId) throw new Error("Ingen overlevering valgt");
      const template = templates.find((t) => t.child_id === activeChildId) ?? templates.find((t) => !t.child_id) ?? templates[0];
      if (!template) throw new Error("Ingen mal funnet");
      const exchangeAt = buildExchangeAt(activeHandoverDate, activeHandover?.handover_time ?? null, timezone);
      const { data, error } = await supabase.rpc("get_or_create_exchange_for_handover", {
        _family_id: fid,
        _child_id: activeChildId,
        _handover_date: activeHandoverDate,
        _exchange_at: exchangeAt,
        _template_id: template.id,
        _from_parent_user_id: fromParent,
        _to_parent_user_id: toParent,
      });
      if (error) throw error;
      return data as unknown as { id: string; created: boolean; exchange: ExchangeChecklist };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: keys.exchangeForDate(fid, activeChildId!, activeHandoverDate!) });
      queryClient.invalidateQueries({ queryKey: keys.exchanges(fid, activeChildId!) });
      queryClient.invalidateQueries({ queryKey: keys.lines(result.id) });
      queryClient.invalidateQueries({ queryKey: keys.checks(result.id) });
      setHistoryExchangeId(null);
      toast.success(result.created ? "Sjekkliste opprettet" : "Sjekkliste funnet");
    },
    onError: () => toast.error("Kunne ikke opprette sjekkliste"),
  });

  const addLineMutation = useMutation({
    mutationFn: async ({ exchangeId, label }: { exchangeId: string; label: string }) => {
      const maxSort = lines.length > 0 ? Math.max(...lines.map((l) => l.sort_order)) + 1 : 0;
      const { data: line, error: lineErr } = await supabase
        .from("exchange_checklist_lines")
        .insert({
          exchange_checklist_id: exchangeId,
          label,
          sort_order: maxSort,
          added_by_user_id: user!.id,
        })
        .select()
        .single();
      if (lineErr) throw lineErr;
      const { error: checkErr } = await supabase
        .from("exchange_checklist_line_checks")
        .insert({
          exchange_checklist_id: exchangeId,
          exchange_checklist_line_id: line.id,
          checked: false,
        });
      if (checkErr) throw checkErr;
      return line;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.lines(activeExchangeId!) });
      queryClient.invalidateQueries({ queryKey: keys.checks(activeExchangeId!) });
      setNewLineText("");
      toast.success("Lagt til");
    },
    onError: () => toast.error("Kunne ikke legge til"),
  });

  const deleteLineMutation = useMutation({
    mutationFn: async (lineId: string) => {
      const { error } = await supabase.from("exchange_checklist_lines").delete().eq("id", lineId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.lines(activeExchangeId!) });
      queryClient.invalidateQueries({ queryKey: keys.checks(activeExchangeId!) });
    },
    onError: () => toast.error("Kunne ikke slette"),
  });

  const toggleCheckMutation = useMutation({
    mutationFn: async ({ exchangeId, checkId, checked }: { exchangeId: string; checkId: string; checked: boolean }) => {
      const { error } = await supabase
        .from("exchange_checklist_line_checks")
        .update({
          checked,
          checked_by: checked ? user!.id : null,
          checked_at: checked ? new Date().toISOString() : null,
        })
        .eq("id", checkId);
      if (error) throw error;
    },
    onMutate: async ({ exchangeId, checkId, checked }) => {
      const qk = keys.checks(exchangeId);
      await queryClient.cancelQueries({ queryKey: qk });
      const prev = queryClient.getQueryData<LineCheck[]>(qk);
      queryClient.setQueryData<LineCheck[]>(qk, (old = []) =>
        old.map((c) =>
          c.id === checkId
            ? { ...c, checked, checked_by: checked ? user!.id : null, checked_at: checked ? new Date().toISOString() : null }
            : c
        )
      );
      return { prev, qk };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(ctx.qk, ctx.prev);
      toast.error("Kunne ikke oppdatere");
    },
    onSettled: (_d, _e, vars) => {
      queryClient.invalidateQueries({ queryKey: keys.checks(vars.exchangeId) });
    },
  });

  useEffect(() => {
    if (!activeExchangeId) return;
    const channel = supabase
      .channel(`exchange-rt-${activeExchangeId}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "exchange_checklist_line_checks", filter: `exchange_checklist_id=eq.${activeExchangeId}` }, () => {
        queryClient.invalidateQueries({ queryKey: keys.checks(activeExchangeId) });
      })
      .on("postgres_changes", { event: "*", schema: "public", table: "exchange_checklist_lines", filter: `exchange_checklist_id=eq.${activeExchangeId}` }, () => {
        queryClient.invalidateQueries({ queryKey: keys.lines(activeExchangeId) });
        queryClient.invalidateQueries({ queryKey: keys.checks(activeExchangeId) });
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [activeExchangeId, queryClient]);

  const handleAddLine = useCallback(() => {
    const text = newLineText.trim();
    if (!text || !activeExchangeId) return;
    addLineMutation.mutate({ exchangeId: activeExchangeId, label: text });
  }, [newLineText, activeExchangeId, addLineMutation]);

  const checkedCount = checks.filter((c) => c.checked).length;
  const totalCount = lines.length;
  const nowMs = Date.now();
  const pastExchanges = childExchanges.filter((e) => new Date(e.exchange_at).getTime() < nowMs).slice(0, 10);

  if (children.length === 0) {
    return (
      <p className="text-sm text-muted-foreground text-center py-4">
        Ingen barn med delt omsorg registrert ennå.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      {!selectedChildId && (
        <Tabs
          value={activeChildId ?? children[0]?.id}
          onValueChange={(val) => {
            setActiveChildId(val);
            setSelectedHandoverDate(null);
            setHistoryExchangeId(null);
          }}
        >
          <TabsList className="w-full">
            {children.map((child) => (
              <TabsTrigger key={child.id} value={child.id} className="flex-1 gap-1.5">
                <span>{child.avatar_emoji}</span>
                <span>{child.name}</span>
              </TabsTrigger>
            ))}
          </TabsList>

          {children.map((child) => (
            <TabsContent key={child.id} value={child.id} />
          ))}
        </Tabs>
      )}

      {activeChildId && templates.length === 0 && (
        <Card>
          <CardContent className="p-6 text-center space-y-2">
            <p className="text-sm text-muted-foreground">Ingen sjekkliste-maler ennå.</p>
          </CardContent>
        </Card>
      )}

      {activeChildId && templates.length > 0 && upcomingHandovers.length === 0 && !historyExchangeId && (
        <Card>
          <CardContent className="p-6 text-center space-y-2">
            <p className="text-sm text-muted-foreground">Ingen byttedag planlagt ennå.</p>
            <p className="text-xs text-muted-foreground">
              {embeddedInSamarbeid
                ? "Legg inn neste byttedag i samværskalenderen over."
                : "Gå til Samarbeid for å legge inn neste byttedag."}
            </p>
          </CardContent>
        </Card>
      )}

      {activeChildId && templates.length > 0 && activeHandoverDate && !activeExchange && !historyExchangeId && (
        <Card>
          <CardContent className="p-6 text-center space-y-3">
            <p className="text-sm font-medium capitalize">
              Neste overlevering: {formatDateLong(activeHandoverDate + "T12:00:00", timezone)}
            </p>
            {activeHandover?.handover_time && (
              <p className="text-xs text-muted-foreground">Kl. {activeHandover.handover_time}</p>
            )}
            <div className="flex justify-center gap-4 text-xs text-muted-foreground">
              <span>Fra: {getParentLabel(fromParent)}</span>
              <span>Til: {getParentLabel(toParent)}</span>
            </div>
            <Button
              onClick={() => createMutation.mutate()}
              disabled={createMutation.isPending}
              className="rounded-xl"
            >
              {createMutation.isPending
                ? "Oppretter…"
                : `Forbered bytte (${formatDate(activeHandoverDate + "T12:00:00", timezone, { day: "numeric", month: "short" })})`}
            </Button>
          </CardContent>
        </Card>
      )}

      {activeExchange && (
        <div className="space-y-3">
          <Card>
            <CardContent className="p-4 space-y-1">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium capitalize">
                  {formatDate(activeExchange.exchange_at, timezone, {
                    weekday: "long",
                    day: "numeric",
                    month: "long",
                  })}
                </p>
                <span className="text-xs text-muted-foreground tabular-nums">
                  {checkedCount}/{totalCount}
                </span>
              </div>
              <div className="flex gap-3 text-xs text-muted-foreground">
                <span>Fra: {getParentLabel(activeExchange.from_parent_user_id)}</span>
                <span>Til: {getParentLabel(activeExchange.to_parent_user_id)}</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4 space-y-2">
              {lines.length === 0 && (
                <p className="text-xs text-muted-foreground py-2">Ingen ting i sjekklisten ennå. Legg til nedenfor.</p>
              )}
              {lines.map((line) => {
                const check = checkByLineId.get(line.id);
                return (
                  <div key={line.id} className="flex items-center gap-3 py-1.5">
                    <Checkbox
                      checked={check?.checked ?? false}
                      onCheckedChange={(val) => {
                        if (!check) return;
                        toggleCheckMutation.mutate({
                          exchangeId: activeExchangeId!,
                          checkId: check.id,
                          checked: !!val,
                        });
                      }}
                    />
                    <span className={cn("text-sm flex-1 transition-all", check?.checked && "line-through text-muted-foreground")}>
                      {line.label}
                    </span>
                    <button
                      onClick={() => deleteLineMutation.mutate(line.id)}
                      className="text-muted-foreground/50 hover:text-destructive transition-colors p-1"
                      aria-label="Slett"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                );
              })}
              <div className="flex gap-2 pt-2 border-t border-border">
                <Input
                  value={newLineText}
                  onChange={(e) => setNewLineText(e.target.value)}
                  placeholder="Legg til ting…"
                  className="h-8 text-sm"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddLine();
                    }
                  }}
                />
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-8 px-2"
                  onClick={handleAddLine}
                  disabled={!newLineText.trim() || addLineMutation.isPending}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>

          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="rounded-xl text-xs flex-1" onClick={() => setShowExchangePicker(true)}>
              Velg annet bytte
            </Button>
            {historyExchangeId && activeHandoverDate && (
              <Button
                size="sm"
                variant="ghost"
                className="rounded-xl text-xs"
                onClick={() => {
                  setHistoryExchangeId(null);
                  setSelectedHandoverDate(null);
                }}
              >
                Tilbake til neste
              </Button>
            )}
          </div>
        </div>
      )}

      {activeChildId && upcomingHandovers.length > 1 && !historyExchangeId && (
        <div className="flex gap-1.5 overflow-x-auto pb-1">
          {upcomingHandovers.map((h) => (
            <button
              key={h.date}
              onClick={() => {
                setSelectedHandoverDate(h.date);
                setHistoryExchangeId(null);
              }}
              className={cn(
                "text-xs rounded-lg px-2.5 py-1.5 whitespace-nowrap transition-colors border",
                activeHandoverDate === h.date
                  ? "bg-primary/10 text-primary border-primary/30"
                  : "bg-muted/30 text-muted-foreground border-border hover:bg-muted"
              )}
            >
              {formatDate(h.date + "T12:00:00", timezone, { day: "numeric", month: "short" })}
            </button>
          ))}
        </div>
      )}

      <Dialog open={showExchangePicker} onOpenChange={(o) => !o && setShowExchangePicker(false)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Velg bytte</DialogTitle>
            <DialogDescription>Kommende overleveringer og historikk</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 max-h-80 overflow-y-auto">
            {upcomingHandovers.length > 0 && (
              <div className="space-y-1">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Kommende</p>
                {upcomingHandovers.map((h) => (
                  <button
                    key={h.date}
                    className={cn(
                      "w-full text-left px-3 py-2 rounded-lg text-sm transition-colors",
                      activeHandoverDate === h.date && !historyExchangeId ? "bg-primary/10 text-primary" : "hover:bg-muted"
                    )}
                    onClick={() => {
                      setSelectedHandoverDate(h.date);
                      setHistoryExchangeId(null);
                      setShowExchangePicker(false);
                    }}
                  >
                    <span className="capitalize">
                      {formatDate(h.date + "T12:00:00", timezone, { weekday: "short", day: "numeric", month: "short" })}
                    </span>
                    {h.handover_time && <span className="text-xs text-muted-foreground ml-2">kl. {h.handover_time}</span>}
                  </button>
                ))}
              </div>
            )}
            {pastExchanges.length > 0 && (
              <div className="space-y-1">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Historikk</p>
                {pastExchanges.map((ex) => (
                  <button
                    key={ex.id}
                    className={cn(
                      "w-full text-left px-3 py-2 rounded-lg text-sm transition-colors",
                      historyExchangeId === ex.id ? "bg-primary/10 text-primary" : "hover:bg-muted"
                    )}
                    onClick={() => {
                      setHistoryExchangeId(ex.id);
                      setShowExchangePicker(false);
                    }}
                  >
                    <span className="capitalize">
                      {formatDate(ex.exchange_at, timezone, { weekday: "short", day: "numeric", month: "short" })}
                    </span>
                  </button>
                ))}
              </div>
            )}
            {upcomingHandovers.length === 0 && pastExchanges.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4">Ingen bytter registrert ennå.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
